import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useLang } from "../i18n/LanguageContext";
import { LOGO_SRC } from "../i18n/translations";

const NAV_ITEMS = [
  { key: "home", href: "#top" },
  { key: "about", href: "#about" },
  { key: "divisions", href: "#divisions" },
  { key: "sectors", href: "#sectors" },
  { key: "projects", href: "#impact" },
  { key: "contact", href: "#contact" },
];

const Navigation = () => {
  const { lang, setLang, t } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const dark = scrolled || open;

  return (
    <header
      data-testid="site-nav"
      className={`nav-shell fixed top-0 left-0 right-0 z-50 ${
        dark
          ? "bg-white/95 backdrop-blur-xl border-b border-[#E4E7ED] shadow-[0_1px_0_rgba(0,43,107,0.04)]"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-[1480px] mx-auto px-6 md:px-12 h-24 flex items-center justify-between">
        <a
          href="#top"
          data-testid="nav-logo"
          className="flex items-center gap-3"
        >
          <img
            src={LOGO_SRC}
            alt="ARIIVA"
            loading="eager"
            decoding="async"
            className={`h-14 md:h-16 w-auto transition-all ${dark ? "opacity-100" : "brightness-0 invert opacity-95"}`}
          />
        </a>

        <nav className="hidden md:flex items-center gap-9">
          {NAV_ITEMS.map((item) => (
            <a
              key={item.key}
              href={item.href}
              data-testid={`nav-link-${item.key}`}
              className={`text-[12px] tracking-[0.18em] uppercase font-medium link-underline transition-colors ${
                dark ? "text-[#1A1F2C] hover:text-[#002B6B]" : "text-white/90 hover:text-white"
              }`}
            >
              {t.nav[item.key]}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-5">
          <div
            className={`hidden md:flex items-center gap-1.5 text-[11px] tracking-[0.2em] uppercase font-semibold ${
              dark ? "text-[#1A1F2C]" : "text-white/85"
            }`}
          >
            <button
              data-testid="language-toggle-es"
              onClick={() => setLang("es")}
              className={`px-1 transition-opacity ${lang === "es" ? "opacity-100" : "opacity-50 hover:opacity-80"}`}
            >ES</button>
            <span className="opacity-40">/</span>
            <button
              data-testid="language-toggle-en"
              onClick={() => setLang("en")}
              className={`px-1 transition-opacity ${lang === "en" ? "opacity-100" : "opacity-50 hover:opacity-80"}`}
            >EN</button>
          </div>

          <a
            href="#contact"
            data-testid="nav-cta-button"
            className={`hidden md:inline-flex items-center text-[11px] tracking-[0.2em] uppercase font-semibold px-5 py-3 transition-all ${
              dark
                ? "bg-[#002B6B] text-white hover:bg-[#6F2DBD]"
                : "bg-white text-[#002B6B] hover:bg-[#F7F8FA]"
            }`}
          >
            {t.nav.cta}
          </a>

          <button
            onClick={() => setOpen((v) => !v)}
            data-testid="nav-mobile-toggle"
            className={`md:hidden p-2 ${dark ? "text-[#1A1F2C]" : "text-white"}`}
            aria-label="menu"
          >
            <div className="w-6 h-px bg-current mb-1.5" />
            <div className="w-6 h-px bg-current mb-1.5" />
            <div className="w-4 h-px bg-current ml-auto" />
          </button>
        </div>
      </div>

      {open && (
        <motion.div
          initial={{ opacity: 0, y: -8 }}
          animate={{ opacity: 1, y: 0 }}
          className="md:hidden bg-white border-t border-[#E4E7ED]"
          data-testid="nav-mobile-panel"
        >
          <div className="flex flex-col px-6 py-6 gap-4">
            {NAV_ITEMS.map((item) => (
              <a
                key={item.key}
                href={item.href}
                onClick={() => setOpen(false)}
                className="text-[#1A1F2C] text-sm tracking-[0.18em] uppercase font-medium"
              >
                {t.nav[item.key]}
              </a>
            ))}
            <div className="flex items-center gap-2 text-xs tracking-[0.2em] pt-3 border-t border-[#E4E7ED]">
              <button onClick={() => setLang("es")} className={lang === "es" ? "font-bold" : "opacity-60"}>ES</button>
              <span className="opacity-30">/</span>
              <button onClick={() => setLang("en")} className={lang === "en" ? "font-bold" : "opacity-60"}>EN</button>
            </div>
          </div>
        </motion.div>
      )}
    </header>
  );
};

export default Navigation;
